import { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import TicketEditModal from "./TicketEditModal";

const apiScopes = [
  "api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user",
];

export default function SessionDashboard({ onBackToTimeTracker, onNavigateLegacy }) {
  const { instance, accounts } = useMsal();
  const [me, setMe] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionJobId, setActionJobId] = useState(null);

  // Sync UI state
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);

  // Pieces finished input tracking
  const [piecesByJob, setPiecesByJob] = useState({});

  // Job context store (indexed by jobId)
  const [jobContext, setJobContext] = useState({});

  // History filters
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all"); // "all", "today", "week", "month"
  const [syncFilter, setSyncFilter] = useState("all"); // "all", "synced", "pending", "failed"

  // Edit modal state
  const [editingTicket, setEditingTicket] = useState(null);

  // Tick to drive live elapsed timers
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Poll for session updates every 5 seconds (syncs with Presence HUD)
  useEffect(() => {
    const pollId = setInterval(() => {
      loadDashboard();
    }, 5000);
    return () => clearInterval(pollId);
  }, [accounts, instance]);

  async function acquireAccessToken() {
    if (accounts.length === 0) {
      throw new Error("No logged in user.");
    }
    const account = accounts[0];
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account,
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn("Session expired, redirecting to login...");
        await instance.loginRedirect({
          scopes: apiScopes,
          account,
        });
        return null;
      }
      throw error;
    }
  }

  async function loadDashboard() {
    try {
      setLoading(true);
      const accessToken = await acquireAccessToken();
      const authHeader = { Authorization: `Bearer ${accessToken}` };

      const meRes = await fetch("/api/me", { headers: authHeader });
      const meData = await meRes.json();
      setMe(meData);

      const tRes = await fetch("/api/me/time/tickets", { headers: authHeader });
      const tData = await tRes.json();
      setTickets(tData.tickets || []);

      const aRes = await fetch("/api/me/time/active", { headers: authHeader });
      const aData = await aRes.json();
      setActive(aData.sessions || {});
    } catch (err) {
      console.error("Error loading session dashboard:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDashboard();
  }, [accounts, instance]);

  // Update on time-start/pause/resume/complete
  useEffect(() => {
    function handleTimeUpdated() {
      loadDashboard();
    }
    window.addEventListener("time-updated", handleTimeUpdated);
    return () => {
      window.removeEventListener("time-updated", handleTimeUpdated);
    };
  }, [accounts, instance]);

  // Listen for job-context broadcasts
  useEffect(() => {
    function receiveJobContext(event) {
      const ctx = event.detail;
      if (!ctx || !ctx.jobId) return;

      setJobContext((prev) => ({
        ...prev,
        [ctx.jobId]: {
          jobNumber: ctx.jobNumber,
          jobName: ctx.jobName,
          orderNumber: ctx.orderNumber,
          quoteNumber: ctx.quoteNumber,
          projectName: ctx.projectName,
          quantityToMake: ctx.quantityToMake,
          productCode: ctx.productCode,
          qty: ctx.qty,
        },
      }));
    }

    window.addEventListener("job-context", receiveJobContext);
    return () => window.removeEventListener("job-context", receiveJobContext);
  }, []);

  // --------------------------
  // SYNC — only clear completed active sessions
  // --------------------------
  async function syncToJobBoss() {
    try {
      setSyncLoading(true);
      const accessToken = await acquireAccessToken();

      const res = await fetch("/api/me/time/sync", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      let data = null;
      try {
        data = await res.json();
      } catch {}

      if (!res.ok) {
        console.error("Sync to JobBOSS failed:", data);
        setSyncSummary({ error: "Sync failed. See console for details." });
      } else {
        setSyncSummary({
          processed: data?.processed ?? 0,
          synced: data?.synced ?? 0,
          failed: data?.failed ?? 0,
        });

        // Reload dashboard data (gets fresh active + tickets)
        await loadDashboard();

        // PRUNE completed sessions ONLY (defensive)
        setActive((prev) => {
          const newState = {};
          for (const [jobId, session] of Object.entries(prev)) {
            if (session.state !== "completed") {
              newState[jobId] = session;
            }
          }
          return newState;
        });
      }
    } catch (err) {
      console.error("Error syncing to JobBOSS:", err);
      setSyncSummary({ error: "Error syncing to JobBOSS." });
    } finally {
      setSyncLoading(false);
    }
  }

  // Save edited ticket
  async function saveTicketEdit(ticketId, updates) {
    const accessToken = await acquireAccessToken();

    const res = await fetch(`/api/me/time/tickets/${ticketId}`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || "Failed to update ticket");
    }

    // Reload dashboard to show updated data
    await loadDashboard();
  }

  async function callTimeAction(jobId, action, extraBody = {}) {
    try {
      setActionJobId(jobId);
      const accessToken = await acquireAccessToken();

      const res = await fetch(`/api/jobs/${jobId}/time/${action}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(extraBody),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error(`Failed to ${action} job ${jobId}`, data);
      }

      await loadDashboard();

      // Auto-sync on complete
      if (action === "complete") {
        await syncToJobBoss();

        // Notify entire app to refresh its job/session state
        window.dispatchEvent(new Event("time-updated"));
      }
    } catch (err) {
      console.error(`Error calling ${action} for job ${jobId}:`, err);
    } finally {
      setActionJobId(null);
    }
  }

  function formatTime(iso) {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString();
    } catch {
      return iso;
    }
  }

  // Compute elapsed seconds for an active session
  function computeElapsedSeconds(session) {
    if (!session) return 0;

    // Get accumulated time from previous pause/resume cycles (floor to remove decimals)
    const accumulated = Math.floor(session.accumulatedSeconds || 0);

    if (session.state === "paused" || session.state === "completed") {
      // When paused/completed, durationSeconds should already include accumulated time
      return typeof session.durationSeconds === "number"
        ? Math.floor(session.durationSeconds)
        : accumulated;
    }

    if (session.state === "running") {
      const startMs = new Date(session.startTime).getTime();
      const nowMs = Date.now();
      const currentSegment = Math.floor((nowMs - startMs) / 1000);
      // Total time = accumulated from previous segments + current segment
      return accumulated + (currentSegment > 0 ? currentSegment : 0);
    }

    return accumulated;
  }

  function formatElapsed(seconds) {
    const total = Math.max(0, Math.floor(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  // REAL sync status using syncedToJobBoss + syncError
  // Note: PostgreSQL returns boolean, SQLite returned integer 0/1
  function getSyncStatus(t) {
    if (t.syncedToJobBoss === true || t.syncedToJobBoss === 1)
      return { icon: "✅", label: "Synced", color: "#1a7f37", status: "synced" };
    if (t.syncError)
      return { icon: "⚠️", label: "Failed", color: "#b3261e", status: "failed" };
    return { icon: "⏳", label: "Pending", color: "#b26a00", status: "pending" };
  }

  // Filter tickets based on search, date range, and sync status
  const filteredTickets = useMemo(() => {
    return tickets.filter((t) => {
      // Date filter
      if (dateFilter !== "all") {
        const ticketDate = t.startTime ? new Date(t.startTime) : null;
        if (!ticketDate) return false;

        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfWeek = new Date(startOfToday);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        if (dateFilter === "today" && ticketDate < startOfToday) return false;
        if (dateFilter === "week" && ticketDate < startOfWeek) return false;
        if (dateFilter === "month" && ticketDate < startOfMonth) return false;
      }

      // Sync status filter
      if (syncFilter !== "all") {
        const { status } = getSyncStatus(t);
        if (syncFilter !== status) return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const ctx = jobContext[t.jobId] || {};
        const searchFields = [
          t.quoteNumber,
          ctx.quoteNumber,
          t.jobNumber,
          ctx.jobNumber,
          t.jobName,
          ctx.jobName,
          t.projectName,
          ctx.projectName,
          t.operationCode,
          t.productCode,
          ctx.productCode,
        ].filter(Boolean).map(s => String(s).toLowerCase());

        if (!searchFields.some(field => field.includes(query))) {
          return false;
        }
      }

      return true;
    });
  }, [tickets, dateFilter, syncFilter, searchQuery, jobContext]);

  // Sync status counts
  const syncCounts = useMemo(() => {
    const counts = { synced: 0, pending: 0, failed: 0 };
    for (const t of tickets) {
      const { status } = getSyncStatus(t);
      counts[status]++;
    }
    return counts;
  }, [tickets]);

  if (loading && !me) return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh",
      color: "#6b7280",
      fontSize: 14,
    }}>
      Loading session history...
    </div>
  );

  // touch tick so timers re-render each second
  void tick;

  const userName = me?.azureUser?.name || me?.azureUser?.preferred_username;
  const employeeId = me?.jobBossEmployeeId;
  const activeEntries = Object.entries(active || {});

  // Group active sessions by sessionId (for group cards)
  const groupedActive = (() => {
    const groups = {};
    for (const [jobId, session] of activeEntries) {
      const key = session.sessionId || `single-${jobId}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push([jobId, session]);
    }
    return Object.entries(groups).map(([sessionId, items]) => ({
      sessionId,
      items,
    }));
  })();

  return (
    <div
      style={{
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 24,
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      {/* BACK BUTTON */}
      <button
        onClick={() => {
          if (onBackToTimeTracker) {
            // Direct callback from WireframeApp (when rendered via showSessionHistory state)
            onBackToTimeTracker();
          } else {
            // Hash navigation for ViewRenderer-based rendering
            window.location.hash = '#/time-tracking/time-tracker';
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 20px",
          backgroundColor: "#7c3aed",
          color: "white",
          border: "none",
          borderRadius: 8,
          fontSize: 15,
          fontWeight: 600,
          cursor: "pointer",
          alignSelf: "flex-start",
          boxShadow: "0 2px 4px rgba(124, 58, 237, 0.3)",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back to Time Tracker
      </button>

      {/* HEADER */}
      <header style={{ backgroundColor: "white", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <h2
          style={{
            marginBottom: 4,
            color: "#7c3aed",
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          Session History
        </h2>
        {userName && (
          <div style={{ fontSize: 14, color: "#6b7280" }}>
            Signed in as <strong style={{ color: "#374151" }}>{userName}</strong>
            {employeeId ? ` · Employee #${employeeId}` : null}
          </div>
        )}

        <div
          style={{
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={syncToJobBoss}
            disabled={syncLoading}
            style={{
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 500,
              backgroundColor: "#7c3aed",
              color: "white",
              border: "none",
              borderRadius: 6,
              cursor: syncLoading ? "not-allowed" : "pointer",
              opacity: syncLoading ? 0.6 : 1,
            }}
          >
            {syncLoading ? "Syncing..." : "Sync to JobBOSS"}
          </button>

          {syncSummary && !syncSummary.error && (
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              Synced {syncSummary.synced}/{syncSummary.processed} tickets
              {syncSummary.failed ? `, ${syncSummary.failed} failed` : ""}
            </span>
          )}

          {syncSummary?.error && (
            <span style={{ fontSize: 12, color: "#dc2626", display: "flex", alignItems: "center", gap: 8 }}>
              {syncSummary.error}
              <button
                onClick={syncToJobBoss}
                disabled={syncLoading}
                style={{
                  padding: "4px 12px",
                  fontSize: 12,
                  backgroundColor: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: syncLoading ? "not-allowed" : "pointer",
                  opacity: syncLoading ? 0.5 : 1,
                }}
              >
                Retry Sync
              </button>
            </span>
          )}
        </div>
      </header>

      {/* Active Jobs / Groups */}
      {groupedActive.length > 0 && (
        <section style={{ backgroundColor: "white", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
          <h3
            style={{
              marginBottom: 12,
              color: "#374151",
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Active Jobs
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {groupedActive.map(({ sessionId, items }) => {
              const isGroup = items.length > 1;
              const representativeSession = items[0][1];
              const operationCode =
                representativeSession.operationCode || "(none)";
              const startTime = representativeSession.startTime;
              const groupState = representativeSession.state;

              return (
                <div
                  key={sessionId}
                  style={{
                    border: "2px solid #f97316",
                    borderRadius: 8,
                    padding: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    backgroundColor: "#fff7ed",
                  }}
                >
                  {/* GROUP HEADER */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 4,
                    }}
                  >
                    <div style={{ color: "#374151" }}>
                      <strong style={{ color: "#1f2937" }}>
                        {isGroup
                          ? `Group Session (${items.length} jobs)`
                          : "Single Session"}
                      </strong>{" "}
                      · Operation: {operationCode}
                      <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 12 }}>
                        Started: {formatTime(startTime)} · State: {groupState}
                      </span>
                    </div>

                    {/* GROUP ACTION BUTTONS */}
                    {groupState !== "completed" && (
                      <div style={{ display: "flex", gap: 8 }}>
                        {groupState === "running" && (
                          <button
                            onClick={async () => {
                              for (const [jId] of items) {
                                await callTimeAction(jId, "pause");
                              }
                            }}
                            disabled={actionJobId !== null}
                            style={{
                              backgroundColor: "#eab308",
                              color: "#000",
                              border: "none",
                              padding: "6px 12px",
                              borderRadius: 4,
                              cursor: "pointer",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {actionJobId !== null ? "Pausing..." : "Pause All"}
                          </button>
                        )}
                        {groupState === "paused" && (
                          <button
                            onClick={async () => {
                              for (const [jId] of items) {
                                await callTimeAction(jId, "resume");
                              }
                            }}
                            disabled={actionJobId !== null}
                            style={{
                              backgroundColor: "#22c55e",
                              color: "#fff",
                              border: "none",
                              padding: "6px 12px",
                              borderRadius: 4,
                              cursor: "pointer",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {actionJobId !== null ? "Resuming..." : "Resume All"}
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            for (const [jId] of items) {
                              const ctx = jobContext[jId] || {};
                              const piecesVal = piecesByJob[jId] ?? ctx.qty ?? 0;
                              await callTimeAction(jId, "complete", {
                                piecesFinished: piecesVal,
                              });
                            }
                          }}
                          disabled={actionJobId !== null}
                          style={{
                            backgroundColor: "#dc2626",
                            color: "#fff",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {actionJobId !== null ? "Completing..." : "Complete All"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* JOBS WITHIN GROUP */}
                  {items.map(([jobId, session]) => {
                    const isRunning = session.state === "running";
                    const isPaused = session.state === "paused";
                    const isCompleted = session.state === "completed";

                    const ctx = jobContext[jobId] || {};
                    const qtyToMake =
                      ctx.quantityToMake !== undefined &&
                      ctx.quantityToMake !== null
                        ? ctx.quantityToMake
                        : null;

                    const jobLabel =
                      ctx.jobNumber ||
                      session.jobNumber ||
                      session.jobName ||
                      ctx.jobName ||
                      jobId;

                    const piecesValue =
                      piecesByJob[jobId] === undefined
                        ? ""
                        : piecesByJob[jobId];

                    const elapsedSeconds = computeElapsedSeconds(session);
                    const elapsedLabel = formatElapsed(elapsedSeconds);

                    return (
                      <div
                        key={jobId}
                        style={{
                          borderTop: "1px solid #fed7aa",
                          paddingTop: 8,
                          marginTop: 4,
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          color: "#374151",
                        }}
                      >
                        <div>
                          <strong style={{ color: "#1f2937" }}>Job:</strong> {jobLabel}
                        </div>
                        <div>
                          <strong style={{ color: "#1f2937" }}>State:</strong> {session.state}
                        </div>
                        <div>
                          <strong style={{ color: "#1f2937" }}>Elapsed:</strong> {elapsedLabel}
                        </div>

                        {/* Quantity + Pieces Finished */}
                        {!isCompleted && (
                          <div
                            style={{
                              marginTop: 4,
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              flexWrap: "wrap",
                              fontSize: 12,
                            }}
                          >
                            <div>
                              <strong style={{ color: "#1f2937" }}>Quantity to Make:</strong>{" "}
                              {qtyToMake !== null ? qtyToMake : "N/A"}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <label style={{ fontSize: 12, color: "#1f2937", fontWeight: 600 }}>
                                Pieces Finished:
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={piecesValue}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setPiecesByJob((prev) => ({
                                    ...prev,
                                    [jobId]:
                                      value === ""
                                        ? undefined
                                        : Math.max(0, Number(value) || 0),
                                  }));
                                }}
                                style={{
                                  width: 80,
                                  padding: "4px 8px",
                                  fontSize: 12,
                                  backgroundColor: "white",
                                  color: "#1f2937",
                                  border: "1px solid #d1d5db",
                                  borderRadius: 4,
                                }}
                              />
                            </div>
                          </div>
                        )}

                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Historical Tickets */}
      <section style={{ backgroundColor: "white", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ color: "#374151", fontSize: 15, fontWeight: 600, margin: 0 }}>
            Historical Tickets
          </h3>

          {/* Sync Status Summary */}
          <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
            <span style={{ color: "#16a34a", fontWeight: 500 }}>✓ {syncCounts.synced} synced</span>
            <span style={{ color: "#ca8a04", fontWeight: 500 }}>○ {syncCounts.pending} pending</span>
            <span style={{ color: "#dc2626", fontWeight: 500 }}>! {syncCounts.failed} failed</span>
          </div>
        </div>

        {/* Filter Controls */}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 16,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Search Input */}
          <div style={{ position: "relative", flex: "1 1 200px", maxWidth: 300 }}>
            <input
              type="text"
              placeholder="Search jobs, quotes, operations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px 8px 36px",
                fontSize: 13,
                backgroundColor: "#f9fafb",
                color: "#1f2937",
                border: "1px solid #d1d5db",
                borderRadius: 6,
              }}
            />
            <svg
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: 13,
              backgroundColor: "#f9fafb",
              color: "#1f2937",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>

          {/* Sync Status Filter */}
          <select
            value={syncFilter}
            onChange={(e) => setSyncFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: 13,
              backgroundColor: "#f9fafb",
              color: "#1f2937",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            <option value="all">All Status</option>
            <option value="synced">Synced</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>

          {/* Clear Filters */}
          {(searchQuery || dateFilter !== "all" || syncFilter !== "all") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setDateFilter("all");
                setSyncFilter("all");
              }}
              style={{
                padding: "8px 14px",
                fontSize: 12,
                backgroundColor: "#e5e7eb",
                color: "#374151",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Clear Filters
            </button>
          )}
        </div>

        {tickets.length === 0 ? (
          <div style={{ fontSize: 14, color: "#6b7280", textAlign: "center", padding: 24 }}>
            No time tickets recorded yet.
          </div>
        ) : filteredTickets.length === 0 ? (
          <div style={{ fontSize: 14, color: "#6b7280", padding: 24, textAlign: "center", backgroundColor: "#f9fafb", borderRadius: 8 }}>
            No tickets match your filters.
          </div>
        ) : (
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {/* HEADER ROW */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "90px 100px 100px minmax(0,1fr) 100px 60px 80px 70px 50px",
                padding: "10px 12px",
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: "#f9fafb",
                borderBottom: "1px solid #e5e7eb",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#6b7280",
              }}
            >
              <div>Date</div>
              <div>Quote</div>
              <div>Job</div>
              <div>Name</div>
              <div>Operation</div>
              <div style={{ textAlign: "right" }}>Qty</div>
              <div style={{ textAlign: "right" }}>Duration</div>
              <div style={{ textAlign: "center" }}>Sync</div>
              <div style={{ textAlign: "center" }}></div>
            </div>

            {/* ROWS */}
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {filteredTickets.map((t) => {
                const { icon, label, color, status } = getSyncStatus(t);
                const isFailed = status === "failed";

                const ctx = jobContext[t.jobId] || {};

                const quote = t.quoteNumber || ctx.quoteNumber || "";
                const jobNum = t.jobNumber || ctx.jobNumber || t.jobId;
                const skuName = t.jobName || t.projectName || ctx.jobName || ctx.projectName || "";

                const quantity = t.qty ?? t.piecesFinished ?? ctx.qty ?? ctx.quantityToMake ?? null;

                const durationMinutes = t.durationSeconds != null ? Math.round(t.durationSeconds / 60) : null;
                const durationLabel = durationMinutes != null ? `${durationMinutes}m` : "...";

                // Format date
                const ticketDate = t.startTime ? new Date(t.startTime) : null;
                const dateLabel = ticketDate
                  ? ticketDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  : "—";
                const timeLabel = ticketDate
                  ? ticketDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
                  : "";

                return (
                  <div
                    key={t.ticketId}
                    style={{
                      borderBottom: "1px solid #f3f4f6",
                      backgroundColor: isFailed ? "#fef2f2" : "white",
                      transition: "background-color 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isFailed ? "#fee2e2" : "#f9fafb")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isFailed ? "#fef2f2" : "white")}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "90px 100px 100px minmax(0,1fr) 100px 60px 80px 70px 50px",
                        padding: "10px 12px",
                        fontSize: 13,
                        alignItems: "center",
                        color: "#374151",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: 500, color: "#1f2937" }}>{dateLabel}</span>
                        <span style={{ fontSize: 11, color: "#9ca3af" }}>{timeLabel}</span>
                      </div>
                      <div title={quote} style={{ fontFamily: "monospace", color: "#7c3aed", fontWeight: 500 }}>
                        {quote || "—"}
                      </div>
                      <div title={jobNum} style={{ fontFamily: "monospace", color: "#374151" }}>
                        {jobNum}
                      </div>
                      <div
                        title={skuName || ""}
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          paddingRight: 8,
                          color: "#4b5563",
                        }}
                      >
                        {skuName || <span style={{ color: "#d1d5db" }}>—</span>}
                      </div>
                      <div style={{ fontFamily: "monospace", fontSize: 12, color: "#6b7280" }}>{t.operationCode || "—"}</div>
                      <div style={{ textAlign: "right", fontWeight: 500, color: "#1f2937" }}>
                        {quantity != null ? quantity : "—"}
                      </div>
                      <div style={{ textAlign: "right", fontFamily: "monospace", color: "#374151" }}>{durationLabel}</div>
                      <div
                        style={{
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                        }}
                        title={t.syncError ? `Error: ${t.syncError}` : label}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: color,
                          }}
                        />
                        <span style={{ fontSize: 10, color, fontWeight: 500 }}>{label}</span>
                      </div>
                      {/* Edit button - only for completed tickets */}
                      <div style={{ textAlign: "center" }}>
                        {t.state === "completed" && (
                          <button
                            onClick={() => setEditingTicket(t)}
                            style={{
                              padding: "4px 8px",
                              fontSize: 10,
                              backgroundColor: "transparent",
                              color: "#7c3aed",
                              border: "1px solid #7c3aed",
                              borderRadius: 4,
                              cursor: "pointer",
                              fontWeight: 500,
                            }}
                            title="Edit ticket"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Show error message if failed */}
                    {t.syncError && (
                      <div
                        style={{
                          padding: "4px 12px 10px 12px",
                          fontSize: 11,
                          color: "#dc2626",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                        </svg>
                        {t.syncError}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer with count */}
            <div
              style={{
                padding: "10px 12px",
                fontSize: 12,
                backgroundColor: "#f9fafb",
                borderTop: "1px solid #e5e7eb",
                color: "#6b7280",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>
                Showing {filteredTickets.length} of {tickets.length} tickets
              </span>
              {filteredTickets.length !== tickets.length && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setDateFilter("all");
                    setSyncFilter("all");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#7c3aed",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  Show all
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Edit Ticket Modal */}
      {editingTicket && (
        <TicketEditModal
          ticket={editingTicket}
          onSave={saveTicketEdit}
          onClose={() => setEditingTicket(null)}
        />
      )}
    </div>
  );
}
